import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { CurrencyLimitData } from '../../typings/constants';
import { TrafficVolumeDataItem } from '../../typings/domain';
import { EditableCell } from '../EditableCell/EditableCell';
import { MoneyText } from '../MoneyText/MoneyText';
import { PhrasePricePopupEditor } from '../PhrasePricePopupEditor/PhrasePricePopupEditor';
import {
    TrafaretPhrasePriceEditorPopup,
    TrafaretPhrasePriceEditorPopupSaveValue
} from '../TrafaretPhrasePriceEditorPopup/TrafaretPhrasePriceEditorPopup';

export enum PhraseMoneyCellEditorTypes {
    simple = 'simple',
    trafaretAuction = 'trafaretAuction'
}

interface PhraseMoneyCellEditorBaseProps {
    currency: string;
    bid?: number;
    error?: string;
    isSaving: boolean;
    onCancel: () => void;
    priceLimit: CurrencyLimitData;
    isGridScrolling: boolean;
    cls?: string;
}

export interface PhraseMoneyCellSimpleEditorProps extends PhraseMoneyCellEditorBaseProps {
    type: PhraseMoneyCellEditorTypes.simple;
    onSave: (value: number) => void;
}

export interface PhraseMoneyCellTrafaretAuctionEditorProps extends PhraseMoneyCellEditorBaseProps {
    type: PhraseMoneyCellEditorTypes.trafaretAuction;
    onSave: (value: TrafaretPhrasePriceEditorPopupSaveValue) => void;
    trafficVolumeData: Array<TrafficVolumeDataItem>;
}

export type PhraseMoneyCellEditorProps = PhraseMoneyCellSimpleEditorProps | PhraseMoneyCellTrafaretAuctionEditorProps;

interface PhraseMoneyCellEditorState {
    isPopupOpened: boolean;
}

export class PhraseMoneyCellEditor extends React.Component<PhraseMoneyCellEditorProps, PhraseMoneyCellEditorState> {
    static getDerivedStateFromProps(nextProps: PhraseMoneyCellEditorProps) {
        if (nextProps.isGridScrolling) {
            return {
                isPopupOpened: false
            };
        }

        return null;
    }

    componentDidUpdate(prevProps: PhraseMoneyCellEditorProps) {
        if (
            (prevProps.isSaving !== this.props.isSaving || prevProps.error !== this.props.error) &&
            !this.props.isSaving &&
            !this.props.error
        ) {
            this.setState({
                isPopupOpened: false
            });
        }
    }

    constructor(props: PhraseMoneyCellEditorProps) {
        super(props);

        this.state = {
            isPopupOpened: false
        };
    }

    render() {
        const { isPopupOpened } = this.state;
        const { bid, cls } = this.props;

        return (
            <>
                <EditableCell
                    onClick={this.switchPopupState}
                    isActive={isPopupOpened}
                    cls={classNames(cls, Bem.cls('phrase-money-cell-editor'))}
                >
                    {bid ? <MoneyText num={bid} fractionalPartLength={2} /> : '–'}
                </EditableCell>
                {this.getPopup()}
            </>
        );
    }

    private switchPopupState = () => {
        const { isPopupOpened } = this.state;

        this.setState({
            isPopupOpened: !isPopupOpened
        });
    };

    private getPopup = () => {
        const { isPopupOpened } = this.state;
        const props = this.props;
        const bid = props.bid || 0;

        switch (props.type) {
            case PhraseMoneyCellEditorTypes.trafaretAuction:
                return (
                    <TrafaretPhrasePriceEditorPopup
                        priceLimit={props.priceLimit}
                        initialBid={bid}
                        error={props.error}
                        trafficVolumeData={props.trafficVolumeData}
                        currency={props.currency}
                        anchor={this}
                        isPopupOpen={isPopupOpened}
                        isSaving={props.isSaving}
                        onSave={props.onSave}
                        onCancel={this.cancel}
                    />
                );
            default:
                return (
                    <PhrasePricePopupEditor
                        priceLimit={props.priceLimit}
                        price={bid}
                        error={props.error}
                        currency={props.currency}
                        anchor={this}
                        isPopupOpen={isPopupOpened}
                        isSaving={props.isSaving}
                        onSave={props.onSave}
                        onCancel={this.cancel}
                    />
                );
        }
    };

    private cancel = () => {
        this.setState({
            isPopupOpened: false
        });

        this.props.onCancel();
    };
}
